import React, { useEffect, useRef, useState, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from '../DashboardPage.module.css';
import { SUPRANATIONALS, SUPRANATIONAL_MEMBERS, isAllowed } from '../../../utils/jurisdictionScope';
import { Legend, NO_DATA_COLOR, choroplethColor, choroplethSteps } from '../../../components/Legend';
import * as dashboardApi from '../../../api/dashboardApi';
import { useTheme } from '../../../context/ThemeContext';

const GEOJSON_URL =
    'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson';

// Aggregate jurisdiction stats by country ISO2 code
function aggregateByCountry(stats: dashboardApi.JurisdictionStats[]): Map<string, number> {
    const map = new Map<string, number>();
    stats.forEach(s => {
        const code = (s.jurisdiction_code || '').toUpperCase();
        const country = code.includes('-') ? code.split('-')[0] : code;
        if (country.length >= 2) {
            map.set(country, (map.get(country) || 0) + s.regulation_count);
        }
    });
    return map;
}

function getColor(count: number, max: number, isDark: boolean): string {
    // SRS #20: one shared scale for fill and legend (components/Legend.tsx).
    if (!count) return isDark ? 'var(--ds-text-secondary)' : NO_DATA_COLOR;
    return choroplethColor(count, max);
}

interface TooltipState {
    x: number; y: number;
    name: string; count: number;
}

interface CountryOption {
    code: string;
    name: string;
    count: number;
    bounds: L.LatLngBounds;
}

const GlobalMap: React.FC<{
    stats: dashboardApi.JurisdictionStats[];
    countryStats?: Record<string, number>;
    isLoading: boolean;
    /** SRS #16: ISO-2 / supranational codes the reader's plan covers; null or undefined = everything. */
    allowedCountryCodes?: string[] | null;
    selectedLocation?: { lat: number; lng: number } | null;
    companyJurisdictions?: string[];
    showCompanyMarkers?: boolean;
    onCountryClick?: (code: string, name: string) => void;
    /** Where a locked jurisdiction sends the reader (pricing by default). */
    onLockedClick?: (code: string, name: string) => void;
    /** SRS #10: optional live layers (activity / upcoming / consultations / enforcement) — per-ISO counts; "all" = countryStats. */
    layers?: Record<string, Record<string, number>>;
    layerLabels?: Record<string, string>;
}> = ({ stats, countryStats = {}, isLoading, allowedCountryCodes = null, selectedLocation = null, companyJurisdictions = [], showCompanyMarkers = false, onCountryClick, onLockedClick, layers, layerLabels = {} }) => {
    const [activeLayer, setActiveLayer] = useState<string>('all');
    const layerStats = activeLayer !== 'all' && layers?.[activeLayer] ? layers[activeLayer] : countryStats;
    const layerNoun = activeLayer === 'all' ? 'regulation' : activeLayer === 'consultations' ? 'open consultation' : activeLayer === 'upcoming' ? 'upcoming law' : activeLayer === 'enforcement' ? 'effective date' : 'recent action';
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const geoLayerRef = useRef<L.GeoJSON | null>(null);
    const featureLayersRef = useRef<Map<string, L.Path>>(new Map());
    const { theme } = useTheme();
    // Map always uses light colors as per original design
    const mapIsDark = false;
    // Filter uses current theme
    const filterIsDark = theme === 'dark';
    const [geoData, setGeoData] = useState<any>(null);
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);
    const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [scaleMax, setScaleMax] = useState<number>(1);
    const markersRef = useRef<L.LayerGroup | null>(null);

    // Country coordinates for major jurisdictions - wrapped in useMemo for performance
    const countryCoordinates = useMemo(() => ({
        'United States': [38.9072, -77.0369] as [number, number],
        'USA': [38.9072, -77.0369] as [number, number],
        'United Kingdom': [51.5074, -0.1278] as [number, number],
        'UK': [51.5074, -0.1278] as [number, number],
        'Germany': [52.5200, 13.4050] as [number, number],
        'France': [48.8566, 2.3522] as [number, number],
        'Italy': [41.9028, 12.4964] as [number, number],
        'Spain': [40.4168, -3.7038] as [number, number],
        'Netherlands': [52.3676, 4.9041] as [number, number],
        'Belgium': [50.8503, 4.3517] as [number, number],
        'Switzerland': [47.3769, 8.5417] as [number, number],
        'Austria': [48.2082, 16.3738] as [number, number],
        'Sweden': [59.3293, 18.0686] as [number, number],
        'Norway': [59.9139, 10.7522] as [number, number],
        'Denmark': [55.6761, 12.5683] as [number, number],
        'Finland': [60.1699, 24.9384] as [number, number],
        'Poland': [52.2297, 21.0122] as [number, number],
        'Czech Republic': [50.0755, 14.4378] as [number, number],
        'Hungary': [47.4979, 19.0402] as [number, number],
        'Romania': [44.4268, 26.1025] as [number, number],
        'Bulgaria': [42.6977, 23.3219] as [number, number],
        'Greece': [37.9838, 23.7275] as [number, number],
        'Portugal': [38.7223, -9.1393] as [number, number],
        'Ireland': [53.3498, -6.2603] as [number, number],
        'Luxembourg': [49.6151, 6.1296] as [number, number],
        'Estonia': [59.4370, 24.7536] as [number, number],
        'Latvia': [56.9496, 24.1052] as [number, number],
        'Lithuania': [54.6872, 25.2797] as [number, number],
        'Slovakia': [48.1486, 17.1077] as [number, number],
        'Slovenia': [46.0569, 14.5058] as [number, number],
        'Croatia': [45.8150, 15.9785] as [number, number],
        'Cyprus': [35.1856, 33.3823] as [number, number],
        'Malta': [35.9375, 14.3754] as [number, number],
        'Canada': [45.4215, -75.6972] as [number, number],
        'Australia': [-35.2809, 149.1300] as [number, number],
        'New Zealand': [-41.2865, 174.7762] as [number, number],
        'Japan': [35.6762, 139.6503] as [number, number],
        'South Korea': [37.5665, 126.9780] as [number, number],
        'China': [39.9042, 116.4074] as [number, number],
        'India': [28.6139, 77.2090] as [number, number],
        'Singapore': [1.3521, 103.8198] as [number, number],
        'Malaysia': [3.1390, 101.6869] as [number, number],
        'Thailand': [13.7563, 100.5018] as [number, number],
        'Indonesia': [-6.2088, 106.8456] as [number, number],
        'Philippines': [14.5995, 120.9842] as [number, number],
        'Vietnam': [21.0285, 105.8542] as [number, number],
        'Hong Kong': [22.3193, 114.1694] as [number, number],
        'Taiwan': [25.0330, 121.5654] as [number, number],
        'United Arab Emirates': [25.2048, 55.2708] as [number, number],
        'UAE': [25.2048, 55.2708] as [number, number],
        'Saudi Arabia': [24.7136, 46.6753] as [number, number],
        'Qatar': [25.2854, 51.5310] as [number, number],
        'Kuwait': [29.3759, 47.9774] as [number, number],
        'Bahrain': [26.0667, 50.5577] as [number, number],
        'Oman': [23.5859, 58.4059] as [number, number],
        'Jordan': [31.9539, 35.9106] as [number, number],
        'Lebanon': [33.8938, 35.5018] as [number, number],
        'Israel': [31.7683, 35.2137] as [number, number],
        'Turkey': [41.0082, 28.9784] as [number, number],
        'Egypt': [30.0444, 31.2357] as [number, number],
        'South Africa': [-26.2041, 28.0473] as [number, number],
        'Nigeria': [6.5244, 3.3792] as [number, number],
        'Kenya': [-1.2921, 36.8219] as [number, number],
        'Morocco': [31.7917, -7.0926] as [number, number],
        'Brazil': [-15.8267, -47.9218] as [number, number],
        'Mexico': [19.4326, -99.1332] as [number, number],
        'Argentina': [-34.6037, -58.3816] as [number, number],
        'Chile': [-33.4489, -70.6693] as [number, number],
        'Colombia': [4.7110, -74.0721] as [number, number],
        'Peru': [-12.0464, -77.0428] as [number, number],
        'Venezuela': [10.4806, -66.9036] as [number, number],
        'Russia': [55.7558, 37.6173] as [number, number],
        'Ukraine': [50.4501, 30.5234] as [number, number],
        'Belarus': [53.9045, 27.5615] as [number, number],
        'Kazakhstan': [51.1605, 71.4704] as [number, number],
        'Uzbekistan': [41.2995, 69.2401] as [number, number],
        'Pakistan': [33.6844, 73.0479] as [number, number],
        'Bangladesh': [23.8103, 90.4125] as [number, number],
        'Sri Lanka': [6.9271, 79.8612] as [number, number],
        'Myanmar': [16.8409, 96.1952] as [number, number],
        'Cambodia': [11.5564, 104.9282] as [number, number],
        'Laos': [17.9757, 102.6331] as [number, number],
    }), []);

    // Fetch world GeoJSON once
    useEffect(() => {
        fetch(GEOJSON_URL)
            .then(r => r.json())
            .then(setGeoData)
            .catch(() => {});
    }, []);

    // Init Leaflet map
    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        const map = L.map(containerRef.current, {
            center: [20, 10],
            zoom: 2,
            minZoom: 1.5,
            maxZoom: 8,
            zoomControl: false,
            attributionControl: false,
            worldCopyJump: false,
            scrollWheelZoom: false,
            doubleClickZoom: false,
            touchZoom: false,
            boxZoom: false,
            keyboard: false,
        });
        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Rebuild choropleth whenever data or theme changes
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !geoData) return;

        if (geoLayerRef.current) {
            geoLayerRef.current.remove();
            geoLayerRef.current = null;
        }
        featureLayersRef.current.clear();

        const resolvedCountryMap: Map<string, number> = activeLayer !== 'all' || Object.keys(layerStats).length > 0
            ? new Map(Object.entries(layerStats).map(([k, v]) => [k.toUpperCase(), v]))
            : aggregateByCountry(stats);
        const max = Math.max(...Array.from(resolvedCountryMap.values()), 1);
        setScaleMax(max);

        // Build country options for dropdown (only countries with data)
        const options: CountryOption[] = [];

        const layer = L.geoJSON(geoData as any, {
            style: (feature) => {
                const iso = (feature?.properties?.['ISO3166-1-Alpha-2'] || '').toUpperCase();
                const count = resolvedCountryMap.get(iso) || 0;
                const locked = count > 0 && !isAllowed(iso, allowedCountryCodes);
                return {
                    fillColor: getColor(count, max, mapIsDark),
                    fillOpacity: locked ? 0.3 : count > 0 ? 0.85 : (mapIsDark ? 0.7 : 0.5),
                    color: mapIsDark ? 'var(--ds-text-secondary)' : 'var(--ds-text-faint)',
                    weight: mapIsDark ? 0.8 : 1,
                };
            },
            onEachFeature: (feature, featureLayer) => {
                const iso = (feature?.properties?.['ISO3166-1-Alpha-2'] || '').toUpperCase();
                const name = feature?.properties?.name || iso;
                const count = resolvedCountryMap.get(iso) || 0;

                // Store layer reference for highlight
                if (iso) featureLayersRef.current.set(iso, featureLayer as L.Path);

                // Collect bounds for dropdown
                if (count > 0) {
                    try {
                        const bounds = (featureLayer as any).getBounds?.() as L.LatLngBounds | undefined;
                        if (bounds) {
                            options.push({ code: iso, name, count, bounds });
                        }
                    } catch {}
                }

                const clickable = Boolean(onCountryClick) && count > 0;
                featureLayer.on({
                    mouseover: (e) => {
                        (e.target as L.Path).setStyle({
                            fillOpacity: 1,
                            weight: 1.5,
                            color: mapIsDark ? 'var(--ds-text-secondary)' : 'var(--ds-text-faint)',
                        });
                        const el = (e.target as any).getElement?.() as SVGElement | undefined;
                        if (el) el.style.cursor = clickable ? 'pointer' : '';
                        const rect = containerRef.current?.getBoundingClientRect();
                        if (rect) {
                            const pt = e.containerPoint as L.Point;
                            setTooltip({ x: pt.x, y: pt.y, name, count });
                        }
                    },
                    mouseout: (e) => {
                        layer.resetStyle(e.target as L.Path);
                        setTooltip(null);
                    },
                    // Drill-down: click a country with data to filter the dashboard to it (QA #9).
                    // SRS #16: outside the plan → upgrade path instead of a silent no-op.
                    click: () => {
                        if (!clickable) return;
                        if (isAllowed(iso, allowedCountryCodes)) onCountryClick!(iso, name);
                        else onLockedClick?.(iso, name);
                    },
                });
            },
        });

        layer.addTo(map);
        geoLayerRef.current = layer;

        // Supranationals (EU, GCC, ASEAN…) have no polygon of their own: their
        // bounds are the union of their members' so "jump" actually goes somewhere.
        for (const [code, name] of Object.entries(SUPRANATIONALS)) {
            const count = resolvedCountryMap.get(code) || 0;
            if (count <= 0) continue;
            let bounds: L.LatLngBounds | undefined;
            for (const member of SUPRANATIONAL_MEMBERS[code] || []) {
                const b = (featureLayersRef.current.get(member) as any)?.getBounds?.() as L.LatLngBounds | undefined;
                if (!b) continue;
                bounds = bounds ? bounds.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
            }
            if (bounds) options.push({ code, name, count, bounds });
        }

        // Sort by count desc, update dropdown options
        options.sort((a, b) => b.count - a.count);
        setCountryOptions(options);
        setSelectedCountry('');
    }, [geoData, stats, countryStats, layerStats, activeLayer, mapIsDark, allowedCountryCodes]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Add/update company jurisdiction markers
    useEffect(() => {
        const map = mapRef.current;
        console.log('Marker effect triggered:', { showCompanyMarkers, companyJurisdictions, mapExists: !!map });
        
        if (!map || !showCompanyMarkers || companyJurisdictions.length === 0) {
            console.log('Skipping markers - conditions not met');
            if (markersRef.current) {
                markersRef.current.eachLayer((layer) => {
                    markersRef.current?.removeLayer(layer);
                });
                markersRef.current = null;
            }
            return;
        }

        // Remove existing markers
        if (markersRef.current) {
            markersRef.current.eachLayer((layer) => {
                markersRef.current?.removeLayer(layer);
            });
        }

        // Create new marker group
        const markerGroup = L.layerGroup();
        markersRef.current = markerGroup;

        // Add markers for each company jurisdiction
        companyJurisdictions.forEach(jurisdiction => {
            console.log('Processing jurisdiction:', jurisdiction);
            const coords = countryCoordinates[jurisdiction as keyof typeof countryCoordinates];
            console.log('Coordinates found:', coords);
            
            if (coords) {
                // Create custom location pin icon
                const customIcon = L.divIcon({
                    html: '<div style="width: 20px; height: 20px; background: var(--ds-brand-blue); border: 2px solid white; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
                    className: 'company-location-pin',
                    iconSize: [20, 20],
                    iconAnchor: [10, 20],
                    popupAnchor: [0, -20],
                });

                const marker = L.marker(coords, { icon: customIcon })
                    .bindPopup(`
                        <div style="text-align: center; padding: 4px;">
                            <strong style="color: #1d2939; font-size: 14px;">${jurisdiction}</strong><br>
                            <span style="color: var(--ds-text-muted); font-size: 12px;">Company Jurisdiction</span>
                        </div>
                    `);

                markerGroup.addLayer(marker);
                console.log('Added marker for:', jurisdiction);
            } else {
                console.log('No coordinates found for:', jurisdiction);
            }
        });

        markerGroup.addTo(map);
        console.log('Marker group added to map with', markerGroup.getLayers().length, 'markers');

        return () => {
            if (markersRef.current) {
                markersRef.current.eachLayer((layer) => {
                    markersRef.current?.removeLayer(layer);
                });
                markersRef.current = null;
            }
        };
    }, [showCompanyMarkers, companyJurisdictions, countryCoordinates]);

    // Highlight selected country with yellow border
    useEffect(() => {
        const layer = geoLayerRef.current;
        if (!layer) return;
        const target = selectedCountry?.toUpperCase();
        layer.eachLayer((l: any) => {
            const iso = (l.feature?.properties?.['ISO3166-1-Alpha-2'] || '').toUpperCase();
            if (target && iso === target) {
                l.setStyle({ color: 'var(--ds-link)', weight: 3, fillOpacity: 1 });
                l.bringToFront?.();
            } else {
                layer.resetStyle(l);
            }
        });
    }, [selectedCountry]);

    // Pan to external selected location
    useEffect(() => {
        if (!mapRef.current) return;
        if (selectedLocation) {
            mapRef.current.flyTo([selectedLocation.lat, selectedLocation.lng], 5, { duration: 1 });
        } else {
            mapRef.current.flyTo([20, 10], 2, { duration: 0.8 });
        }
    }, [selectedLocation]);

    // SRS #16: "Jump to" now does three things — zooms the map, highlights the
    // jurisdiction and filters the dashboard to it (the same drill-down as a
    // map click). A jurisdiction outside the plan is shown but locked.
    const allowedOptions = countryOptions.filter(o => isAllowed(o.code, allowedCountryCodes));
    const lockedOptions = countryOptions.filter(o => !isAllowed(o.code, allowedCountryCodes));

    const handleCountrySelect = (code: string) => {
        if (!code) {
            setSelectedCountry('');
            mapRef.current?.flyTo([20, 10], 2, { duration: 0.8 });
            return;
        }
        const option = countryOptions.find(o => o.code === code);
        if (!option) return;
        if (!isAllowed(code, allowedCountryCodes)) {
            onLockedClick?.(code, option.name);
            return;
        }
        setSelectedCountry(code);
        if (option.bounds && mapRef.current) {
            mapRef.current.flyToBounds(option.bounds, { padding: [40, 40], duration: 1, maxZoom: 6 });
        }
        onCountryClick?.(code, option.name);
    };

    const bgColor = mapIsDark ? '#101828' : '#f0f5ff';   // the map is a light artwork by design; chips and tooltip follow the theme

    return (
        <div className={styles.mapWrapper} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {layers && (
                <div role="tablist" aria-label="Map layer" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="map-layers">
                    {[['all', 'All regulations'], ...Object.keys(layers).map(k => [k, layerLabels[k] || k])].map(([k, label]) => (
                        <button key={k} type="button" role="tab" aria-selected={activeLayer === k} onClick={() => setActiveLayer(k)}
                            style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, cursor: 'pointer', border: `1px solid ${activeLayer === k ? 'var(--ds-brand-blue)' : 'var(--ds-border-strong)'}`, background: activeLayer === k ? 'var(--ds-brand-blue)' : 'transparent', color: activeLayer === k ? 'var(--ds-text-on-dark)' : 'inherit' }}>
                            {label}
                        </button>
                    ))}
                </div>
            )}
            {/* Map + legend overlay */}
            <div style={{ position: 'relative' }}>
                <div
                    ref={containerRef}
                    className={styles.mapContainer}
                    style={{ background: bgColor }}
                    onWheel={(e) => {
                        // Leaflet intercepts wheel events; forward them to the nearest scrollable ancestor
                        let el: HTMLElement | null = (e.currentTarget as HTMLElement).parentElement;
                        while (el) {
                            if (el.scrollHeight > el.clientHeight) { el.scrollTop += e.deltaY; break; }
                            el = el.parentElement;
                        }
                    }}
                />

                {/* Hover tooltip */}
                {tooltip && (
                    <div style={{
                        position: 'absolute',
                        left: Math.min(tooltip.x + 14, (containerRef.current?.clientWidth ?? 600) - 180),
                        top: Math.max(tooltip.y - 60, 8),
                        zIndex: 9999,
                        background: 'var(--ds-surface)',
                        border: '1px solid var(--ds-border)',
                        borderRadius: 10,
                        padding: '10px 16px',
                        pointerEvents: 'none',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        minWidth: 150,
                    }}>
                        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--ds-text)' }}>
                            {tooltip.name}
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--ds-text-muted)', marginTop: 3 }}>
                            {tooltip.count > 0
                                ? `${tooltip.count} ${layerNoun}${tooltip.count !== 1 ? 's' : ''}`
                                : activeLayer === 'all' ? 'No regulations tracked' : `No ${layerNoun}s`}
                        </div>
                    </div>
                )}

                {/* Loading overlay */}
                {isLoading && (
                    <div style={{
                        position: 'absolute', inset: 0, zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(240, 245, 255, 0.7)',
                        borderRadius: 12,
                    }}>
                        <span style={{ fontSize: 14, color: 'var(--ds-text-muted)' }}>Loading map…</span>
                    </div>
                )}

                {/* Legend — bottom-left inside map (SRS #20: numeric ranges from the shared scale) */}
                {!isLoading && (
                    <div style={{
                        position: 'absolute', bottom: 10, left: 12, zIndex: 1000,
                        background: 'rgba(255,255,255,0.92)',
                        border: '1px solid var(--ds-border)',
                        borderRadius: 8, padding: '6px 10px',
                    }}>
                        <Legend compact title="Regulations tracked" items={[
                            ...choroplethSteps(scaleMax).map(st => ({ color: st.color, label: st.label })),
                            { color: NO_DATA_COLOR, label: 'None' },
                            ...(allowedCountryCodes != null ? [{ color: '', label: 'Outside your plan', shape: 'lock' as const, hint: 'Shown faded — select it to see upgrade options' }] : []),
                        ]} />
                    </div>
                )}
            </div>

            {/* SRS #16: jurisdiction navigation — quick chips for the reader's own jurisdictions + grouped picker */}
            {!isLoading && countryOptions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    {allowedCountryCodes != null && allowedOptions.length > 0 && (
                        <div data-testid="jurisdiction-chips" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
                            {allowedOptions.slice(0, 8).map(o => {
                                const active = selectedCountry === o.code;
                                return (
                                    <button key={o.code} type="button" onClick={() => handleCountrySelect(active ? '' : o.code)} aria-pressed={active}
                                        style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer',
                                            border: `1px solid ${active ? 'var(--ds-brand-blue)' : (filterIsDark ? 'var(--ds-text-secondary)' : 'var(--ds-border-strong)')}`,
                                            background: active ? 'var(--ds-brand-blue)' : (filterIsDark ? '#1d2939' : 'var(--ds-surface)'),
                                            color: active ? 'var(--ds-text-on-dark)' : (filterIsDark ? 'var(--ds-border)' : 'var(--ds-text-secondary)') }}>
                                        {o.name} <span style={{ opacity: 0.7 }}>{o.count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, width: '100%' }}>
                        <select
                            value={selectedCountry}
                            onChange={e => handleCountrySelect(e.target.value)}
                            aria-label="Jump to jurisdiction"
                            style={{
                                padding: '6px 14px',
                                borderRadius: 8,
                                border: `1px solid ${filterIsDark ? 'var(--ds-text-secondary)' : 'var(--ds-border-strong)'}`,
                                background: filterIsDark ? '#1d2939' : 'var(--ds-surface)',
                                color: filterIsDark ? 'var(--ds-border)' : 'var(--ds-text)',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                outline: 'none',
                                maxWidth: 300,
                                width: '100%',
                            }}
                        >
                            <option value="">— Jump to jurisdiction —</option>
                            {allowedCountryCodes == null ? (
                                countryOptions.map(o => (
                                    <option key={o.code} value={o.code}>{o.name} ({o.count} regulation{o.count !== 1 ? 's' : ''})</option>
                                ))
                            ) : (<>
                                <optgroup label="Your jurisdictions">
                                    {allowedOptions.map(o => (
                                        <option key={o.code} value={o.code}>{o.name} ({o.count} regulation{o.count !== 1 ? 's' : ''})</option>
                                    ))}
                                    {allowedOptions.length === 0 && <option value="" disabled>None in your plan yet</option>}
                                </optgroup>
                                {lockedOptions.length > 0 && (
                                    <optgroup label="Not in your plan — select to see upgrade options">
                                        {lockedOptions.map(o => (
                                            <option key={o.code} value={o.code}>🔒 {o.name} ({o.count})</option>
                                        ))}
                                    </optgroup>
                                )}
                            </>)}
                        </select>
                        {selectedCountry && (
                            <button
                                onClick={() => handleCountrySelect('')}
                                title="Reset to world view"
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: 8,
                                    border: `1px solid ${filterIsDark ? 'var(--ds-text-secondary)' : 'var(--ds-border-strong)'}`,
                                    background: filterIsDark ? '#1d2939' : 'var(--ds-surface)',
                                    color: filterIsDark ? 'var(--ds-border-strong)' : 'var(--ds-text-muted)',
                                    fontSize: '0.82rem',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                ✕ Reset
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalMap;
